
import { FindifyDataContextType } from "@/contexts/FindifyDataContext"
import { getTranslatedString } from "./FindifyTranslatedString"
import { getApiFromModule } from "./GetContentsByModule"
import { Pressable, Share, View } from "react-native"
import FontAwesome6Icons from "react-native-vector-icons/FontAwesome6";
import { FindifyToast } from "../components/FindifyToast";
import * as AddCalendarEvent from 'react-native-add-calendar-event';
import { PlaceColors } from "./PlaceColors";
import { PlaceText } from "../components/PlaceText";
import { getFileUrl } from "@/config/environment";

export const makeDateString = (date: any) => {
    if (date instanceof Date && !isNaN(date.valueOf())) {
        return date.toLocaleString('default', { day: '2-digit', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
    return undefined

}

export const makeStartTimeString = (date: any) => {
    if (date instanceof Date && !isNaN(date.valueOf())) {
        return date.toLocaleString('default', { hour: '2-digit', minute: '2-digit' })
    }
    return undefined

}

export const makeMuseumHourDateString = (dateStart: Date, dateEnd: Date) => {
    return dateStart.toLocaleString('default', { hour: '2-digit', minute: '2-digit' })
        + ' - ' + dateEnd.toLocaleString('default', { hour: '2-digit', minute: '2-digit' });
}

export const makeMuseumDaysString = (dateStart: Date, dateEnd: Date) => {
    return dateStart.toLocaleString('default', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' - ' + dateEnd.toLocaleString('default', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
export const getUniqueDays = (events: any, setSameYearDates: any) => {
    const days: string[] = [];
    let year = undefined;
    let sameYear = true;
    const datetimes = events.map((e: any) => {
        return e.start_date;
    });

    for (const datetime of datetimes) {
        const date = datetime.split('T')[0];
        if (!days.includes(date)) {
            days.push(date);
        }
        if (year === undefined) {
            year = date.split('-')[0];
        } else if (sameYear && year !== date.split('-')[0]) {
            sameYear = false;
        }
    }
    setSameYearDates(sameYear);
    return days;
}

export const makeContentTitle = (content: any, module: string, findifyDataContext: FindifyDataContextType) => {
    switch (module) {
        case 'points_of_interest':
        case 'routes':
            return getTranslatedString(content.title, findifyDataContext);
        default:
            return getTranslatedString(content.name, findifyDataContext);
    }
}

export const getContentIdForModule = (content: any, module: string) => {
    switch (module) {
        case 'events':
            return content?.event_id;
        case 'points_of_interest':
            return content?.point_of_interest_id;
        case 'routes':
            return content?.route_id;
        case 'contents':
        default:
            return content?.content_id;
    }
}

export const makeContentDate = (content: any, module: string) => {
    switch (module) {
        case 'points_of_interest':
        case 'routes':
            return ""
        case 'events':
            const startDate = new Date(content.start_date);
            const endDate = new Date(content.end_date);
            // IF same day just show date once
            if (startDate.getDate() === endDate.getDate()) {
                return startDate.toLocaleString('default', { day: '2-digit', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' - ' + endDate.toLocaleString('default', { hour: '2-digit', minute: '2-digit' });
            }
            return startDate.toLocaleString('default', { day: '2-digit', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) +
                ' - '
                + endDate.toLocaleString('default', { day: '2-digit', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        case 'conents':
        default:
            const date = new Date(content.public_date);
            return date.toLocaleString('default', { day: '2-digit', month: 'numeric', year: 'numeric' });
    }
}


export type ContentDetailsType = {
    image: string,
    title: string,
    resume: string,
    description: string,
    dateString: string,
    hideDate: boolean,
    navigation: any,
    rightActions: JSX.Element,
    content: any,
    findifyDataContext: any,
    module: string,
    t: any,
    isLogged?: boolean
}

export const PlaceContentDetailsInitialUseEffect = (
    props: any,
    findifyDataContext: FindifyDataContextType,
    setContent: Function,
    setContentSlug: Function,
    setContentModule: Function,
    setHideDate: Function,
) => {
    if (props.route.params.content_id || props.route.params.page_slug) {
        const pageSlug = props.route.params.page_slug;
        const contentId = props.route.params.content_id;
        const module = props.route.params.module;
        const api = getApiFromModule(module, findifyDataContext);
        const loadByListFallback = async () => {
            if (!pageSlug || !api?.getList) return null;
            const listData = await api.getList(0, 100, null, null, null);
            return listData?.results?.find((item: any) => item?.page_slug === pageSlug) ?? null;
        }

        api?.get(contentId ?? pageSlug, null, null).then(async (res: any) => {
            if (!res || (typeof res === 'string' && res.trim() === '')) {
                res = await loadByListFallback();
            }

            if (!res) {
                setContent({ __loadError: true, module, page_slug: pageSlug, content_id: contentId });
                setContentSlug(pageSlug);
                setContentModule(module);
                setHideDate(false);
                return;
            }

            if (module === 'events') {
                const resTickets = await getApiFromModule('tickets_collections', findifyDataContext).getList(0, 10, null, {
                    event_id: res.event_id
                }, null).catch((err: any) => {
                    console.log(err);
                    const dummyResponse: any = { results: [] };
                    return dummyResponse;
                });

                const ticketCollections = resTickets.results;
                res.ticket_collections = ticketCollections;
            }
            // Keep navigation metadata available to the details renderer. RMC uses
            // this to switch from the territory menu to the selected nucleus menu.
            if (props.route.params.museum_slug) res = { ...res, __museum_slug: props.route.params.museum_slug };
            setContent(res);
            setContentSlug(pageSlug);
            setContentModule(module);
            setHideDate(res?.categories?.find((cat: any) => cat.category.hide_date_on_details == true));
        }).catch((error: any) => {
            console.error('Failed to load content details:', error);
            setContent({ __loadError: true, module, page_slug: pageSlug, content_id: contentId });
            setContentSlug(pageSlug);
            setContentModule(module);
            setHideDate(false);
        })
    }
}

const makeActionBottomButton = (action: Function, label: string, icon: string, findifyDataContext: FindifyDataContextType) => {
    return <Pressable
        style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            padding: 10,
            backgroundColor: PlaceColors(findifyDataContext).background.get(),
            // backgroundColor: 'red',
        }}
        onPress={() => action()}>
        <FontAwesome6Icons style={{ padding: 3 }} name={icon} size={20} color={PlaceColors(findifyDataContext).text.get()} />
        <PlaceText style={{ fontSize: 16, color: PlaceColors(findifyDataContext).text.get() }}>{label}</PlaceText>
    </Pressable>;
}

const addEventToCalendar = (t: any, content: any, findifyDataContext: FindifyDataContextType) => {
    const title = getTranslatedString(content.name, findifyDataContext);
    const details: AddCalendarEvent.CreateOptions = {
        // id: `FINDIFY_EVENT_${content.workspace_id}_${content.event_id}`, // This must be a phone calendar id, not anything by the dev
        title: title,
        startDate: content.start_date,
        endDate: content.end_date,
        location: content.place_address,
        notes: getTranslatedString(content.resume, findifyDataContext),
    };
    AddCalendarEvent.presentEventCreatingDialog(details)
        .then((eventInfo) => {
            // console.log('added event', eventInfo);
            // handle success - receives an object with `calendarItemIdentifier` and `eventIdentifier` keys, both of type string.
            // These are two different identifiers on iOS.
            // On Android, where they are both equal and represent the event id, also strings.
            // when { action: 'CANCELED' } is returned, the dialog was dismissed
            if (eventInfo.action === 'SAVED') {
                FindifyToast({
                    type: 'success',
                    title: t('schedule.event_added'),
                    message: t('schedule.event_added_sucessfully'),
                })
            } else {
                FindifyToast({
                    type: 'error',
                    title: t('schedule.error_add_event'),
                    message: t('schedule.error_add_event_sucessfully'),

                })
            }

        })
        .catch((error: string) => {
            // handle error such as when user rejected permissions
            // console.warn(error);
            FindifyToast({
                type: 'error',
                title: t('schedule.error_add_event'),
                message: t('schedule.error_add_event_sucessfully'),

            })
        });

}


export const makeEventDate = (selectedLanguage: any, startDate: any, endDate: any) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if start and end dates are on the same day
    const sameDay = start.toDateString() === end.toDateString();

    // Format the start date
    const formattedStartDate = start.toLocaleDateString(selectedLanguage, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });

    // Format the start hour
    const startHour = start.toLocaleTimeString(selectedLanguage, {
        hour: 'numeric',
        minute: 'numeric',
    });

    // Format the end hour
    const endHour = end.toLocaleTimeString(selectedLanguage, {
        hour: 'numeric',
        minute: 'numeric',
    });

    if (sameDay) {
        // If start and end dates are on the same day, return formatted start date with both hours
        return `${formattedStartDate}, ${startHour} - ${endHour}`;
    } else {
        // If start and end dates are on different days, format both dates and hours
        const formattedEndDate = end.toLocaleDateString(selectedLanguage, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });

        // Return formatted start and end dates with respective hours
        return `${formattedStartDate}, ${startHour} - ${formattedEndDate}, ${endHour}`;
    }
};

export const getFeaturedImageUrl = (content: any) => {
    const media = content?.featured_image_media ?? content?.image_media ?? content?.media;
    const source = media?.url
        ?? media?.file_url_path
        ?? media?.file_path
        ?? content?.featured_image_url
        ?? content?.image_url
        ?? (typeof content?.image === 'string' ? content.image : undefined);

    if (!source || typeof source !== 'string') return undefined;
    if (/^https?:\/\//i.test(source)) return source;
    return `${getFileUrl()}${source.startsWith('/') ? source : `/${source}`}`;
}

export const MakeContentDetailsContentVars = (
    content: any,
    module: string,
    findifyDataContext: FindifyDataContextType,
    t: Function,
    navigation: any,
) => {
    let image = getFeaturedImageUrl(content);
    let title = makeContentTitle(content, module, findifyDataContext);
    let resume = getTranslatedString(content.resume, findifyDataContext);
    let description = getTranslatedString(content.description, findifyDataContext);
    let dateString: string | undefined = undefined;
    let rightActions = <></>;
    let bottomButtons = <></>;


    switch (module) {
        case 'events':
            dateString = makeEventDate(findifyDataContext.language, content?.start_date, content?.end_date)
            if (content.place_address) {
                resume = content.place_address + (resume ? '\r\n' + resume : '');
            }

            bottomButtons = <View
                style={{

                    backgroundColor: PlaceColors(findifyDataContext).text.get(),
                    borderTopColor: PlaceColors(findifyDataContext).text.get(),
                    borderTopWidth: 1,
                    // padding: 14,
                    width: '100%',
                    flexDirection: 'row',
                    gap: 1
                }}
            >
                {makeActionBottomButton(() => addEventToCalendar(t, content, findifyDataContext), t('schedule.add_to_calendar'), 'calendar-plus', findifyDataContext)}
                {makeActionBottomButton(() => {
                    navigation.push('EventTicketsPage', {
                        content: content,
                        content_id: content.event_id
                    });
                }, t('base.tickets.tickets'), 'ticket', findifyDataContext)}
            </View>

            break;
        case 'points_of_interest':
            const poiDate = new Date(content?.make_public_on);
            title = getTranslatedString(content.title, findifyDataContext);
            dateString = makeDateString(poiDate);
            // return makeEventModuleDetails(content, t, navigation, findifyDataContext); 
            break;
        case 'contents':
        default:
            const contentDate = new Date(content?.public_date);
            dateString = makeDateString(contentDate);
    }

    return {
        image,
        title,
        resume,
        description,
        dateString,
        rightActions,
        bottomButtons
    };
}


export const MakeContentDetailsSetActions = (
    setActionsList: Function,
    title: string,
    findifyDataContext: FindifyDataContextType,
    content: any,
    module: string
) => {
    setActionsList([{
        icon: 'share-outline',
        action: async () => {
            const result = await Share.share({
                title: title + ' - ' + getTranslatedString(findifyDataContext?.workspace?.name, findifyDataContext) + ' - Findify',
                message: `https://${findifyDataContext.workspace?.primary_domain?.domain_name ?? 'links.findify.cloud'}/dynamicLink?workspace_id=${findifyDataContext.workspace?.workspace_id}&type=details&module=${module}&content_id=${content?.content_id}`,
            }, {
                dialogTitle: getTranslatedString(findifyDataContext?.workspace?.name, findifyDataContext) + ' - Findify',
            });
        }
    }]);
}
